import { TransifexI18next } from '@transifex/i18next'
import { createNativeInstance, TxNative } from '@transifex/native'

interface CustomBackendOptions {
  tokens: string[]
  fetchTimeoutMs?: number
}
class CustomBackend extends TransifexI18next {
  constructor(options: CustomBackendOptions) {
    super(options)
  }

  init(_services: unknown, _options: CustomBackendOptions): void {
    this.txs = this.options.tokens.map((token: string) => {
      return createNativeInstance({ token: token, fetchTimeout: this.options.fetchTimeoutMs ?? 0 })
    })
  }

  read(
    language: string,
    _namespace: string,
    callback: (error: Error | null, data: { [key: string]: any } | null) => void
  ): void {
    const txs: TxNative[] = this.txs
    const promises: Promise<void>[] = []
    txs.forEach((tx) => {
      promises.push(tx.fetchTranslations(language))
    })
    Promise.all(promises)
      .then(() => {
        let data: { [key: string]: string } = {}
        txs.forEach((tx) => {
          data = { ...data, ...this._convertPlurals(tx.cache.getTranslations(language)) }
        })
        callback(null, data)
      })
      .catch((error) => {
        callback(error, null)
      })
  }
}

export default function createCustomBackend(options: CustomBackendOptions): typeof TransifexI18next {
  return new CustomBackend(options)
}

/**
 * Loads a language from one Transifex project, plurals split as for the standard translations. Each request is
 * aborted after `fetchTimeoutMs`, and a CDS that keeps answering 202 is polled for at most that long, so a stalled
 * project rejects within about twice `fetchTimeoutMs`.
 */
export function createTransifexLoader(
  token: string,
  fetchTimeoutMs: number
): (language: string) => Promise<Record<string, string>> {
  const options = { tokens: [token], fetchTimeoutMs }
  const backend = new CustomBackend(options)
  backend.init(undefined, options)
  return (language) =>
    new Promise((resolve, reject) => {
      backend.read(language, 'translation', (error, data) => (error ? reject(error) : resolve(data ?? {})))
    })
}
